import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  IconButton,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import { hostname } from "../../utils";

interface DailyMemory {
  id: string;
  uid: string;
  date: string;
  content: string;
}

interface DailyMemoriesTabProps {
  getToken: () => Promise<string>;
}

const DailyMemoriesTab: React.FC<DailyMemoriesTabProps> = ({ getToken }) => {
  const [memories, setMemories] = useState<DailyMemory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(10);

  // Date filter state
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMemory, setEditingMemory] = useState<DailyMemory | null>(null);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMemory, setDeletingMemory] = useState<DailyMemory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchMemories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        perPage: perPage.toString(),
      });

      if (startDate) {
        params.append("startDate", startDate);
      }
      if (endDate) {
        params.append("endDate", endDate);
      }

      const response = await axios.get(
        `${hostname}/personal-assistant/memories?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMemories(response.data.memories);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load daily memories");
    } finally {
      setLoading(false);
    }
  }, [getToken, page, perPage, startDate, endDate]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleApplyFilter = () => {
    setPage(1);
    fetchMemories();
  };

  const handleClearFilter = () => {
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const handleEditClick = (memory: DailyMemory) => {
    setEditingMemory(memory);
    setEditContent(memory.content);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingMemory(null);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingMemory) return;

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/personal-assistant/memories/${editingMemory.date}`,
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setMemories((prev) =>
        prev.map((m) =>
          m.date === editingMemory.date ? { ...m, content: editContent } : m
        )
      );

      handleEditClose();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save memory");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (memory: DailyMemory) => {
    setDeletingMemory(memory);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeletingMemory(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingMemory) return;

    setDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/personal-assistant/memories/${deletingMemory.date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setMemories((prev) => prev.filter((m) => m.date !== deletingMemory.date));
      setTotal((prev) => prev - 1);

      handleDeleteClose();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete memory");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPreviewContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + "...";
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        View and manage daily memory logs created by your assistant.
      </Typography>

      {/* Date Filter */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <CalendarIcon color="action" />
        <TextField
          size="small"
          label="From"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <Button variant="outlined" size="small" onClick={handleApplyFilter}>
          Filter
        </Button>
        {(startDate || endDate) && (
          <Button
            variant="text"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearFilter}
          >
            Clear
          </Button>
        )}
        {total > 0 && (
          <Chip
            label={`${total} memor${total === 1 ? "y" : "ies"}`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : memories.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No daily memories found.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Your assistant will create daily logs as you interact with it.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {memories.map((memory) => (
              <Paper
                key={memory.date}
                sx={{
                  p: 2,
                  backgroundColor: "action.hover",
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                }}
                variant="outlined"
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {formatDate(memory.date)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {getPreviewContent(memory.content)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, ml: 1, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditClick(memory)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(memory)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Edit Memory - {editingMemory && formatDate(editingMemory.date)}
          <IconButton size="small" onClick={handleEditClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            minRows={12}
            maxRows={20}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="Write your memory content here..."
            InputProps={{
              sx: { fontFamily: "monospace", fontSize: "0.875rem" },
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Memory</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the memory for{" "}
            <strong>
              {deletingMemory && formatDate(deletingMemory.date)}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyMemoriesTab;
